/**
 * `useOnlineStatus()` mirrors `navigator.onLine` into React state and
 * subscribes to the `online`/`offline` window events.
 *
 * Caveat: `navigator.onLine` is permissive — it reports "online" if there
 * is *any* network interface, even when the actual upstream is broken.
 * Callers that need stronger guarantees should additionally watch their
 * own request failures via React Query's `isPaused` / error states.
 */
import { useEffect, useState } from 'react'

function readOnlineStatus(): boolean {
  if (typeof navigator === 'undefined') return true
  // navigator.onLine is widely supported and synchronous.
  return navigator.onLine
}

export function useOnlineStatus(): boolean {
  const [online, setOnline] = useState<boolean>(() => readOnlineStatus())

  useEffect(() => {
    const handleOnline = () => setOnline(true)
    const handleOffline = () => setOnline(false)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    // Re-read on mount in case the event fired before subscription.
    setOnline(readOnlineStatus())
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return online
}
